import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid2 as Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Stack,
  Alert,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Psychology as PromptIcon,
  SmartToy as AiIcon,
} from "@mui/icons-material";
import { useState } from "react";
import {
  ChatPromptDto,
  UpdateChatPromptDto,
  AiModelProvider,
} from "@/dto/ChatPromptDto";
import { Yn } from "@/constants/code";

/**
 * 프롬프트 편집 폼 Props
 */
export interface EditFormProps {
  /**
   * 프롬프트 정보
   */
  promptInfo: ChatPromptDto;

  /**
   * 수정 핸들러
   */
  onEdit: (data: UpdateChatPromptDto) => void;

  /**
   * 삭제 핸들러
   */
  onDelete?: () => void;

  /**
   * 로딩 상태
   */
  isLoading?: boolean;
}

/**
 * 프롬프트 편집 폼 컴포넌트
 * @description QNA 채팅 프롬프트를 편집하는 폼입니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const EditForm = ({
  promptInfo,
  onEdit,
  onDelete,
  isLoading = false,
}: EditFormProps) => {
  const [formData, setFormData] = useState<UpdateChatPromptDto>({
    promptKey: promptInfo.promptKey,
    promptContent: promptInfo.promptContent,
    aiModelProvider: promptInfo.aiModelProvider,
    aiModelName: promptInfo.aiModelName,
    aiModelTemperature: promptInfo.aiModelTemperature,
    aiMaxTokens: promptInfo.aiMaxTokens,
    useCondition: promptInfo.useCondition || "",
    priorityOrder: promptInfo.priorityOrder,
    useYn: promptInfo.useYn,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // 폼 검증
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.promptKey?.trim()) {
      newErrors.promptKey = "프롬프트 키는 필수입니다.";
    } else if (formData.promptKey.length > 50) {
      newErrors.promptKey = "프롬프트 키는 50자 이하여야 합니다.";
    }

    if (!formData.promptContent?.trim()) {
      newErrors.promptContent = "프롬프트 내용은 필수입니다.";
    }

    if (formData.aiModelTemperature < 0 || formData.aiModelTemperature > 2) {
      newErrors.aiModelTemperature = "온도는 0.0 ~ 2.0 사이여야 합니다.";
    }

    if (formData.aiMaxTokens < 1 || formData.aiMaxTokens > 32000) {
      newErrors.aiMaxTokens = "최대 토큰은 1 ~ 32,000 사이여야 합니다.";
    }

    if (formData.priorityOrder < 1) {
      newErrors.priorityOrder = "우선순위는 1 이상이어야 합니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 제출 핸들러
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onEdit(formData);
    }
  };

  // 삭제 확인 핸들러
  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    onDelete?.();
  };

  // 입력값 변경 핸들러
  const handleChange = (field: keyof UpdateChatPromptDto, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // 에러 초기화
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // AI 모델별 옵션 가져오기
  const getModelOptions = (provider: AiModelProvider): string[] => {
    switch (provider) {
      case "OPENAI":
        return ["gpt-4", "gpt-4-turbo", "gpt-3.5-turbo"];
      case "ANTHROPIC":
        return ["claude-3-opus", "claude-3-sonnet", "claude-3-haiku"];
      case "GOOGLE":
        return ["gemini-pro", "gemini-pro-vision"];
      default:
        return [];
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Paper sx={{ p: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PromptIcon color="primary" />
            프롬프트 편집
          </Typography>

          {onDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isLoading}
            >
              삭제
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* 기본 정보 */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              기본 정보
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="프롬프트 키 *"
              placeholder="예: GREETING, FAQ_SEARCH"
              value={formData.promptKey}
              onChange={(e) => handleChange("promptKey", e.target.value)}
              error={!!errors.promptKey}
              helperText={
                errors.promptKey || "고유한 프롬프트 식별자를 입력하세요"
              }
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              type="number"
              label="우선순위"
              value={formData.priorityOrder}
              onChange={(e) =>
                handleChange("priorityOrder", parseInt(e.target.value) || 1)
              }
              error={!!errors.priorityOrder}
              helperText={errors.priorityOrder || "낮을수록 높은 우선순위"}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>사용 여부</InputLabel>
              <Select
                value={formData.useYn}
                label="사용 여부"
                onChange={(e) => handleChange("useYn", e.target.value as Yn)}
              >
                <MenuItem value={Yn.Y}>활성</MenuItem>
                <MenuItem value={Yn.N}>비활성</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="프롬프트 내용 *"
              placeholder="AI가 사용할 프롬프트 내용을 입력하세요..."
              value={formData.promptContent}
              onChange={(e) => handleChange("promptContent", e.target.value)}
              error={!!errors.promptContent}
              helperText={errors.promptContent}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="사용 조건"
              placeholder="이 프롬프트가 언제 사용되는지 설명하세요..."
              value={formData.useCondition}
              onChange={(e) => handleChange("useCondition", e.target.value)}
            />
          </Grid>

          {/* AI 모델 설정 */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              <AiIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              AI 모델 설정
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>AI 제공업체</InputLabel>
              <Select
                value={formData.aiModelProvider}
                label="AI 제공업체"
                onChange={(e) =>
                  handleChange(
                    "aiModelProvider",
                    e.target.value as AiModelProvider
                  )
                }
              >
                <MenuItem value="OPENAI">OpenAI</MenuItem>
                <MenuItem value="ANTHROPIC">Anthropic</MenuItem>
                <MenuItem value="GOOGLE">Google</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>AI 모델</InputLabel>
              <Select
                value={formData.aiModelName}
                label="AI 모델"
                onChange={(e) => handleChange("aiModelName", e.target.value)}
              >
                {getModelOptions(formData.aiModelProvider).map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              type="number"
              label="최대 토큰 수"
              value={formData.aiMaxTokens}
              onChange={(e) =>
                handleChange("aiMaxTokens", parseInt(e.target.value) || 4000)
              }
              error={!!errors.aiMaxTokens}
              helperText={errors.aiMaxTokens || "AI 응답의 최대 길이"}
              inputProps={{ min: 1, max: 32000 }}
            />
          </Grid>

          <Grid size={12}>
            <Box sx={{ px: 2 }}>
              <Typography gutterBottom>
                Temperature (창의성): {formData.aiModelTemperature}
              </Typography>
              <Slider
                value={formData.aiModelTemperature}
                onChange={(_, value) =>
                  handleChange("aiModelTemperature", value as number)
                }
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: "정확함 (0.0)" },
                  { value: 1, label: "균형 (1.0)" },
                  { value: 2, label: "창의적 (2.0)" },
                ]}
                sx={{ mt: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                낮을수록 일관되고 정확한 답변, 높을수록 창의적이고 다양한 답변
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* 제출 버튼 */}
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 4, justifyContent: "flex-end" }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isLoading}
            size="large"
          >
            {isLoading ? "저장 중..." : "저장"}
          </Button>
        </Stack>
      </Paper>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>프롬프트 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            정말로 "{promptInfo.promptKey}" 프롬프트를 삭제하시겠습니까?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            삭제된 프롬프트는 복구할 수 없습니다.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

